import { Transition, TransitionGroup } from 'react-transition-group';
import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { breakpoints, spacing } from '@naf/theme';
import { AnimationWrapper } from '@components/skeleton-loaders/AnimationWrapper';
import { MembershipBenefitReference } from '@appTypes/membershipBenefitReferenceType';
import CardBenefitPartner from '@components/CardBenefitPartner/CardBenefitPartner';
import { BenefitLoader } from '@components/skeleton-loaders/BenefitLoader';
import { getPlaceholderList } from '@utils/getPlaceholderList';
import useSelector from '@redux/typedHooks';
import { actions } from '@redux/modules/membershipBenefitSearch';

export const ResultPage = ({
  page,
  disableFavorite = false,
}: {
  page: { data: MembershipBenefitReference[]; meta: { isLoading: boolean } };
  disableFavorite?: boolean;
}) => {
  const dispatch = useDispatch();
  const favoritesOnly = useSelector((state) => state.membershipBenefitSearch.favoritesOnly);
  const placeholderGridRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  return (
    <AnimationGrid>
      {page.meta.isLoading && (
        <Transition
          nodeRef={placeholderGridRef}
          in={page.meta.isLoading}
          timeout={{ enter: 250, exit: 0 }}
          unmountOnExit
          mountOnEnter
        >
          {(state) => (
            <StyledAnimationWrapper ref={placeholderGridRef} $state={state}>
              {getPlaceholderList(12).map((i) => (
                <BenefitLoader key={`article-placeholder-item-${i}`} />
              ))}
            </StyledAnimationWrapper>
          )}
        </Transition>
      )}
      {!page.meta.isLoading && (
        <Transition nodeRef={gridRef} key="data" in={!page.meta.isLoading} timeout={250} unmountOnExit mountOnEnter>
          {(state) => (
            <StyledAnimationWrapper ref={gridRef} $state={state}>
              {Object.keys(page.data).length > 0
                ? page.data.map((item, index) => (
                    <CardBenefitPartner
                      isFlexible
                      benefit={item}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`item-${item.id}-${index}`}
                      disableFavorite={disableFavorite}
                      benefitPosition={index + 1}
                      onFavoriteChange={(isFavorite) => {
                        // Filtering by favorites, so an unfavorited card no longer belongs in this list.
                        if (favoritesOnly && !isFavorite) {
                          dispatch(actions.removeItem({ id: item.id }));
                        }
                      }}
                    />
                  ))
                : null}
            </StyledAnimationWrapper>
          )}
        </Transition>
      )}
    </AnimationGrid>
  );
};

const AnimationGrid = styled(TransitionGroup)`
  grid-column: 1 / -1;
`;

const StyledAnimationWrapper = styled(AnimationWrapper)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${spacing.space24};

  @media (max-width: ${breakpoints.l}) {
    gap: ${spacing.space32} ${spacing.space24};
  }

  @media (max-width: ${breakpoints.m}) {
    grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
    justify-items: center;
  }
`;
